<template>
  <component
    :is="modalComponent"
    v-if="canStartRender"
    v-bind="modalProps"
    :is-visible="isVisible"
  />
</template>

<script>
import { useEventListener } from '@vueuse/core';
import { computed, inject, onMounted, nextTick, reactive, ref, toRef } from 'vue-demi';

export default {
  props: {
    modalName: {
      type: String,
      required: true,
      validator: (modalName) => modalName.endsWith('Modal'),
    },
  },
  setup(props) {
    const modalName = toRef(props, 'modalName');
    const { legacyConnector } = inject('appFacade');
    const canStartRender = ref(false);
    const isVisible = ref(false);
    const modalProps = reactive({});

    const modalComponent = computed(
      () => () => import(`@/views/modals/${modalName.value}/${modalName.value}.vue`),
    );

    const generatePropsWithCallbacks = (propData = {}) => {
      const newProps = { ...propData };
      propData.callbackKeys?.forEach((callbackKey) => {
        newProps[callbackKey] = (...args) => {
          legacyConnector.postMessageToHybrid({
            type: 'modal-callback-triggered',
            message: {
              callbackKey,
              args: JSON.parse(JSON.stringify(args)),
            },
          });
        };
      });
      return newProps;
    };

    const instantiateModal = async (propData = {}) => {
      // Generate props with callbacks
      const generatedProps = generatePropsWithCallbacks(propData);
      // Keep the reactivity but replace the object
      Object.assign(modalProps, generatedProps);

      // Trigger the modal render
      canStartRender.value = true;

      // This is to wait until the props have been initialised
      // to show the modal so the animation can trigger successfully
      // Otherwise we would have to watch for props to change from undefined to the props we want
      await nextTick();
      isVisible.value = true;
    };

    function modalEventHandler({ data, origin }) {
      if (!window.parent || origin !== window.parent.location.origin) {
        return;
      }

      if (data?.type === 'instantiate-modal') {
        instantiateModal(data.message?.props ?? {});
      }
    }

    function announceAppReady() {
      legacyConnector.postMessageToHybrid({
        type: 'next-app-ready',
      });
    }

    onMounted(() => {
      // setup the event listener to recieve data from the hybrid
      useEventListener(window, 'message', modalEventHandler);

      // fire an event to the hybrid that the modal is ready to be initialised
      announceAppReady();

      if (!legacyConnector.isLegacyConnected.value) {
        // This sets the modal visibility to true for non legacy testing
        instantiateModal();
      }
    });

    return {
      canStartRender,
      modalComponent,
      modalProps,
      isVisible,
    };
  },
};
</script>
